import { cacheClientAttributesForStepWizard } from './services/update-client-attributes';
import { getCurrentFeatureCode } from '@/app/core/auth';
import {
  ClientAttributesSelector,
  ClientAttributesSelectorModel,
} from '@/app/core/components/client-attributes';
import {
  ProgramTransformer,
  ProgramTransformerArgs,
} from '@/app/core/components/program-wizard';
import {
  CancelOperationError,
  getSubCodesForProduct,
  isAnyProductAdmin,
} from '@ssar-app/domain';
import { Modal } from '@ssar-app/uikit';

export const ensureValidCloneFromProgramExists: ProgramTransformer = async (
  args: ProgramTransformerArgs,
) => {
  const {
    program,
    programWizardMetadata: { isClone, from },
  } = args;
  if (isClone && !program) {
    throw new Error(`Could not find Program with the program id: ${from}`);
  }
  return Promise.resolve({
    ...args,
  });
};

type ShowClientAttributesSelectorProps = {
  subscriberCodes: string[];
  canSearchAndSelect: boolean;
  onClientAttributesAvailable: (args: ClientAttributesSelectorModel) => void;
  onCancel: () => void;
};

const showClientAttributesSelector = (
  props: ShowClientAttributesSelectorProps,
) => {
  const {
    subscriberCodes,
    canSearchAndSelect,
    onClientAttributesAvailable,
    onCancel,
  } = props;

  const decoratedOnSuccess = (args: ClientAttributesSelectorModel) => {
    onClientAttributesAvailable(args);
    modal.destroy();
  };

  const modal = Modal.confirm({
    header:
      canSearchAndSelect || subscriberCodes.length > 1
        ? 'Search/Select a client'
        : 'Loading Client Attributes',
    icon: null,
    contentProps: { maxW: '60vw' },
    showOk: false,
    closeOnOverlayClick: false,
    content: (
      <ClientAttributesSelector
        subscriberCodes={subscriberCodes}
        canSearchAndSelect={canSearchAndSelect}
        onSuccess={decoratedOnSuccess}
      />
    ),
    onCancel: onCancel,
    cancelButtonProps: {
      variant: 'outline',
      size: 'sm',
      _hover: {
        bg: 'white',
        color: 'blue.800',
        borderColor: 'blue.800',
      },
    },
  });
};

export const amendSubscriberCode: ProgramTransformer = async (
  args: ProgramTransformerArgs,
) => {
  return new Promise((resolve, reject) => {
    const {
      program,
      programWizardMetadata,
      userProfile,
      messagesStore: _messagesStore,
      programWizardDependentData,
    } = args;
    const { isClone, isDraft } = programWizardMetadata;
    const profileSubCodes = getSubCodesForProduct(
      getCurrentFeatureCode(),
      userProfile,
    );
    const doesProfileAllowAnySubCode = isAnyProductAdmin(userProfile);

    const effectiveSubCode =
      isClone || isDraft
        ? program?.subCode
        : !doesProfileAllowAnySubCode
        ? profileSubCodes
        : undefined;
    if (!doesProfileAllowAnySubCode && !effectiveSubCode) {
      const errorMessage = `Subscriber Code is not found in ${
        isClone ? 'the program being cloned.' : 'in the user profile'
      }`;
      throw new Error(errorMessage);
    }
    showClientAttributesSelector({
      onClientAttributesAvailable(clientSelectorResult) {
        try {
          const dependentData = cacheClientAttributesForStepWizard(
            programWizardDependentData,
            clientSelectorResult,
          );
          resolve({
            ...args,
            program,
            programWizardDependentData: dependentData,
          });
        } catch (err) {
          reject(err);
        }
      },
      onCancel() {
        reject(
          new CancelOperationError('Client selection step was cancelled.'),
        );
      },
      canSearchAndSelect: doesProfileAllowAnySubCode,
      subscriberCodes:
        (isClone || isDraft) && program?.subCode
          ? [program.subCode]
          : doesProfileAllowAnySubCode
          ? []
          : (profileSubCodes as string[]),
    });
  });
};
